import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { UploadProvider } from './context/UploadContext';
import { AutoUploadProvider } from './context/AutoUploadContext';
import { NotificationProvider } from './context/NotificationContext';
import { ChatProvider } from './context/ChatContext';
import AppRoutes from './routes/AppRoutes';
import ErrorBoundary from './components/common/ErrorBoundary';
import { logClientEvent } from './services/logService';
import './App.css';

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language?.slice(0, 2) || 'en';
  }, [i18n.language]);

  // Catches crashes ErrorBoundary can't (it only sees render errors): stray
  // uncaught exceptions and unhandled promise rejections anywhere in the app.
  useEffect(() => {
    const onError = (event) => {
      logClientEvent('ClientJsError', event.error?.message || event.message || 'Unknown error', {
        metadata: { stack: event.error?.stack, source: event.filename, line: event.lineno, col: event.colno },
      });
    };
    const onUnhandledRejection = (event) => {
      const reason = event.reason;
      logClientEvent('ClientUnhandledRejection', reason?.message || String(reason), {
        metadata: { stack: reason?.stack },
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <ChatProvider>
              <UploadProvider>
                <AutoUploadProvider>
                  <AppRoutes />
                  <ToastContainer position="top-right" autoClose={3000} pauseOnFocusLoss={false} />
                </AutoUploadProvider>
              </UploadProvider>
            </ChatProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
